import { useContext } from "react";
import { useQuery, UseQueryOptions, useMutation } from "@tanstack/react-query";
import {
  getStripeClientSecret,
  StripeOptions,
  getUserPaymentMethods,
  getInvoicePlanPreview,
  InvoicePreview,
  createBillingPortalSession,
  getClientSubscriptionSummary,
  createStripeCheckoutSession,
  getUserOutstandingInvoices,
  getUserPaymentHistory,
} from "./billingApi";
import { AppContext } from "../context/app";
import type { Billing, Invoice, PaymentHistoryItem } from "./billingApi";

export function useGetUserPaymentMethods(userUuid: string) {
  return useQuery<Billing[] | null>({
    queryKey: ["user-payment-methods", userUuid],
    queryFn: () => getUserPaymentMethods(userUuid),
  });
}

export function useGetUserOutstandingInvoices(userUuid: string) {
  return useQuery<Invoice[]>({
    queryKey: ["user-outstanding-invoices", userUuid],
    queryFn: () => getUserOutstandingInvoices(userUuid),
  });
}

export function useGetUserPaymentHistory(userUuid: string) {
  return useQuery<PaymentHistoryItem[]>({
    queryKey: ["user-payment-history", userUuid],
    queryFn: () => getUserPaymentHistory(userUuid),
  });
}

export const useGetStripeClientSecret = (
  clientUuid: string,
  userUuid: string,
  options?: UseQueryOptions<StripeOptions>,
) => {
  return useQuery<StripeOptions>(
    ["stripe-secret", clientUuid, userUuid],
    () => getStripeClientSecret(clientUuid, userUuid),
    options,
  );
};

export const useGetInvoicePlanPreviewQuery = (
  {
    planUuid,
    planPriceUuid,
    billingUuid,
    discountUuid,
    ignoreSetupFee,
    customSetupFee,
    startDate,
  }: {
    planUuid: string;
    planPriceUuid?: string;
    billingUuid: string;
    discountUuid: string;
    ignoreSetupFee: boolean;
    customSetupFee: number;
    startDate: string | undefined;
  },
  options?: UseQueryOptions<InvoicePreview>,
) => {
  const { client } = useContext(AppContext);
  return useQuery<InvoicePreview>(
    [
      "invoice-plan-preview",
      planUuid,
      planPriceUuid,
      billingUuid,
      discountUuid,
      customSetupFee,
      startDate,
    ],
    () =>
      getInvoicePlanPreview({
        clientUuid: client.uuid,
        planUuid,
        planPriceUuid,
        billingUuid,
        discountUuid,
        ignoreSetupFee,
        customSetupFee,
        startDate,
      }),
    options,
  );
};

export const useCreateBillingPortalSession = () => {
  return useMutation({
    mutationFn: async (returnUrl: string) => {
      const response = await createBillingPortalSession(returnUrl);
      return response;
    },
  });
};

export const useGetClientSubscriptionSummary = (clientUuid: string) => {
  return useQuery({
    queryKey: ["active-price-ids", clientUuid],
    queryFn: () => getClientSubscriptionSummary(clientUuid),
  });
};

export const useCreateStripeCheckoutSession = () => {
  return useMutation({
    mutationFn: async ({
      priceId,
      customerId,
      returnUrl,
    }: {
      priceId: string;
      customerId: string;
      returnUrl: string;
    }) => {
      const response = await createStripeCheckoutSession({
        priceId,
        customerId,
        returnUrl,
      });
      return response;
    },
  });
};
